import { cn } from '@web/lib/cn';

export type BoardFilter = 'ai' | 'parked' | 'human' | 'resolved' | null;

/**
 * The strip across the top: `23 AI · 3 parked · 2 human · 41 resolved today`.
 *
 * Each segment is a filter, because the first thing anyone does after reading a
 * count is want to see what it counts. Pressed state is `aria-pressed` plus a
 * solid top rule — a background tint alone would be invisible on the segment
 * that is already carrying the signal hue.
 */
export function SummaryStrip({
  counts,
  active,
  onChange,
  resolvedCapped,
}: {
  counts: { ai: number; parked: number; human: number; resolved: number };
  active: BoardFilter;
  onChange: (next: BoardFilter) => void;
  resolvedCapped: boolean;
}) {
  const segments: { key: Exclude<BoardFilter, null>; label: string; value: number }[] = [
    { key: 'ai', label: 'AI', value: counts.ai },
    { key: 'parked', label: 'parked', value: counts.parked },
    { key: 'human', label: 'human', value: counts.human },
    { key: 'resolved', label: 'resolved today', value: counts.resolved },
  ];

  return (
    <div
      className="flex items-stretch border-b border-rule bg-panel"
      role="group"
      aria-label="Board filters"
    >
      {segments.map((segment) => {
        const on = active === segment.key;
        return (
          <button
            key={segment.key}
            type="button"
            aria-pressed={on}
            onClick={() => {
              onChange(on ? null : segment.key);
            }}
            className={cn(
              'flex min-h-11 flex-1 items-baseline gap-2 border-r border-rule px-3 py-2 text-left last:border-r-0',
              'hover:bg-hover',
              on && 'bg-hover',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'self-stretch',
                on ? 'w-[3px] bg-ink' : 'w-[3px] bg-transparent',
                segment.key === 'parked' && on && 'bg-signal',
              )}
            />
            <span
              className={cn(
                'font-mono text-lg leading-none font-medium tabular-nums',
                segment.key === 'parked' && segment.value > 0 ? 'text-signal' : 'text-ink',
              )}
            >
              {segment.value}
              {segment.key === 'resolved' && resolvedCapped && (
                <span className="text-ink-faint">+</span>
              )}
            </span>
            <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
              {segment.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
